import { useState } from "react";
import { Box, Typography, Chip, Collapse, ButtonBase, alpha, useTheme } from "@mui/material";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";

/**
 * Findings as cards, with the ones that share a subsystem folded into one.
 *
 * Two GPS rules firing is one GPS problem seen twice, and stacking them as
 * equal siblings reads as twice the trouble. A group collapses to the worst
 * severity in it and opens to the individual evidence, so the count on screen
 * matches the count of things to go and check.
 */

/** Which subsystem each rule belongs to. Rules that share one group together. */
const CATEGORY = {
  gps_fix_lost: "GPS",
  gps_quality: "GPS",
  accel_clip: "Vibration",
  vibration: "Vibration",
  battery_sag: "Battery",
  attitude_tracking: "Attitude",
  truncated: "Log integrity",
  logged_errors: "Autopilot errors",
  autonomous_recovery: "Flight mode",
};

const RANK = { critical: 0, warn: 1, info: 2, good: 3 };

export const worstOf = (findings) =>
  findings.reduce((worst, f) => (RANK[f.severity] < RANK[worst] ? f.severity : worst), "good");

/**
 * Group by subsystem, preserving the worst-first order the rules came in.
 * A subsystem with a single finding stays a single card — a "group of 1" is
 * just a card with an extra click in front of it.
 */
export const groupFindings = (findings) => {
  const buckets = new Map();
  for (const f of findings) {
    const key = CATEGORY[f.id] || f.id;
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(f);
  }
  return [...buckets.entries()].map(([category, items]) => ({ category, items }));
};

const toneFor = (theme, severity) =>
  severity === "critical"
    ? theme.palette.error.main
    : severity === "warn"
      ? theme.palette.warning.main
      : severity === "good"
        ? theme.palette.success.main
        : theme.palette.info.main;

const SeverityBadge = ({ severity }) => {
  const theme = useTheme();
  const tone = toneFor(theme, severity);
  return (
    <Chip
      label={severity === "warn" ? "WARNING" : severity.toUpperCase()}
      size="small"
      sx={{
        height: 20,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 0.6,
        color: tone,
        backgroundColor: alpha(tone, 0.14),
        border: `1px solid ${alpha(tone, 0.4)}`,
      }}
    />
  );
};

/** The evidence and the physical check, shared by single cards and group rows. */
const FindingBody = ({ finding, at }) => {
  const theme = useTheme();
  return (
    <>
      <Typography variant="body2" sx={{ mt: 0.75, color: theme.palette.text.primary }}>
        {finding.evidence}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5, color: theme.palette.text.secondary }}>
        {finding.hint}
      </Typography>
      {at && (
        <Typography variant="caption" sx={{ display: "block", mt: 0.5, fontFamily: "monospace", color: theme.palette.text.secondary }}>
          {at}
        </Typography>
      )}
    </>
  );
};

const SeverityIcon = ({ severity, tone }) => {
  const sx = { fontSize: 20, color: tone, mt: 0.25, flexShrink: 0 };
  return severity === "critical" ? <ErrorOutlineOutlinedIcon sx={sx} /> : <WarningAmberOutlinedIcon sx={sx} />;
};

const SingleCard = ({ finding, clockFor }) => {
  const theme = useTheme();
  const tone = toneFor(theme, finding.severity);
  return (
    <Box
      sx={{
        display: "flex",
        gap: 1.5,
        p: 2,
        borderRadius: 2,
        border: `1px solid ${alpha(tone, 0.35)}`,
        backgroundColor: alpha(tone, 0.06),
      }}
    >
      <SeverityIcon severity={finding.severity} tone={tone} />
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
            {finding.title}
          </Typography>
          <SeverityBadge severity={finding.severity} />
        </Box>
        <FindingBody finding={finding} at={clockFor(finding)} />
      </Box>
    </Box>
  );
};

const GroupCard = ({ group, clockFor }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const severity = worstOf(group.items);
  const tone = toneFor(theme, severity);

  return (
    <Box
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        border: `1px solid ${alpha(tone, open ? 0.55 : 0.35)}`,
        backgroundColor: alpha(tone, 0.06),
        transition: "border-color .15s",
      }}
    >
      <ButtonBase
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        sx={{ width: "100%", justifyContent: "flex-start", textAlign: "left", p: 2, gap: 1.5 }}
      >
        {/* Severity spine — the group's worst, readable before any text is */}
        <Box sx={{ width: 3, alignSelf: "stretch", minHeight: 28, borderRadius: 2, backgroundColor: tone, flexShrink: 0 }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <SeverityBadge severity={severity} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
              {group.category}
            </Typography>
            <Chip
              label={group.items.length}
              size="small"
              sx={{ height: 20, minWidth: 20, fontSize: 11, fontWeight: 700, color: tone, backgroundColor: alpha(tone, 0.16) }}
            />
          </Box>
          <Typography variant="body2" sx={{ mt: 0.5, color: theme.palette.text.secondary }}>
            {group.items.map((f) => f.title).join(", ")}
          </Typography>
        </Box>
        <ExpandMoreOutlinedIcon
          sx={{
            fontSize: 20,
            color: theme.palette.text.secondary,
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform .2s",
          }}
        />
      </ButtonBase>

      <Collapse in={open} unmountOnExit>
        <Box sx={{ px: 2, pb: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
          {group.items.map((f) => (
            <Box key={f.id} sx={{ display: "flex", gap: 1.5, pt: 1.5, borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}>
              <SeverityIcon severity={f.severity} tone={toneFor(theme, f.severity)} />
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                    {f.title}
                  </Typography>
                  <SeverityBadge severity={f.severity} />
                </Box>
                <FindingBody finding={f} at={clockFor(f)} />
              </Box>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

const FindingCards = ({ findings, clockFor = () => null }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
    {groupFindings(findings).map((group) =>
      group.items.length === 1 ? (
        <SingleCard key={group.category} finding={group.items[0]} clockFor={clockFor} />
      ) : (
        <GroupCard key={group.category} group={group} clockFor={clockFor} />
      )
    )}
  </Box>
);

export default FindingCards;
